import React, { FC, useState, useCallback, useMemo, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Paper,
} from '@mui/material';
import { Close, Check, Clear } from '@mui/icons-material';
import { LocalizationContext } from 'contexts';
import { documentsClient } from 'clients/documents/documentsClient';
import { CLAVersion } from './VersionComparisonPanel';

export interface VersionSettingsDialogProps {
	open: boolean;
	onClose: () => void;
	versions: CLAVersion[];
	currentClaSlug: string;
	onVersionUpdated?: () => void;
}

const VersionSettingsDialog: FC<VersionSettingsDialogProps> = ({
																																 open,
																																 onClose,
																																 versions,
																																 currentClaSlug,
																																 onVersionUpdated,
																															 }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const { dictionary } = useContext(LocalizationContext);

  const sortedVersions = useMemo(() => {
    return [...versions].sort((a, b) => {
      if (a.published_at && b.published_at) {
        return new Date(b.published_at).getTime() - new Date(a.published_at).getTime();
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [versions]);

  const handleMakeCurrent = useCallback(async (slug: string) => {
    try {
      setLoading(slug);
      await documentsClient.setCurrentVersion(slug);
      onVersionUpdated?.();

      window.location.reload();
    } catch (error) {
      console.error('Failed to set current version:', error);
    } finally {
      setLoading(null);
    }
  }, [onVersionUpdated]);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
						Version Management
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
          <Close/>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#424242', py: 2 }}>
										Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#424242', py: 2, width: 120 }}>
										Is current?
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#424242', py: 2, width: 140 }}>
										Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVersions.map((version) => (
                <TableRow
                  key={version.slug}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&:not(:last-child)': {
                      borderBottom: '1px solid #e0e0e0',
                    },
                  }}
                >
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                      {version.published_at ? formatDate(version.published_at) : formatDate(version.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {version.is_current ? (
                        <Chip
                          icon={<Check sx={{ fontSize: 14 }}/>}
                          label=""
                          size="small"
                          sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            width: 24,
                            height: 24,
                            minWidth: 24,
                            '& .MuiChip-label': { display: 'none' },
                            '& .MuiChip-icon': { margin: 0, color: 'white' },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<Clear sx={{ fontSize: 14 }}/>}
                          label=""
                          size="small"
                          sx={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            width: 24,
                            height: 24,
                            minWidth: 24,
                            '& .MuiChip-label': { display: 'none' },
                            '& .MuiChip-icon': { margin: 0, color: 'white' },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={version.is_current || loading === version.slug}
                      onClick={() => handleMakeCurrent(version.slug)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 2,
                        py: 0.5,
                        borderColor: '#e0e0e0',
                        color: '#666',
                        '&:hover': {
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)',
                        },
                        '&:disabled': {
                          borderColor: '#e0e0e0',
                          color: '#bdbdbd',
                        },
                      }}
                    >
                      {loading === version.slug ? (dictionary?.versionManagement?.settingButton || 'Instellen...') : (dictionary?.versionManagement?.makeCurrentButton || 'Maak huidig')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default VersionSettingsDialog;